import React, { useState, useMemo, useEffect, useCallback, useRef } from "react";
import { Box, Typography, IconButton, Chip, CircularProgress, Alert, Dialog, DialogTitle, DialogContent, DialogActions, Stack, FormControl, Select, MenuItem, Popover, Badge, Divider, Tooltip, useMediaQuery, useTheme } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import FilterListIcon from '@mui/icons-material/FilterList';
import { MuiButton, MuiSelector, MuiChip } from '@ace/ace-ui-web';
import { LocationPin, AccessTime, CalendarMonth, Person } from '@mui/icons-material';
import { Calendar, momentLocalizer, View } from 'react-big-calendar';
import moment from 'moment';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import './calendarReport.css';
import { fetchCalendarReport } from "../../redux/CalendarReportReducer/calendarReportSlice";
import { decideShift, ShiftDecision } from "../../redux/DecisionShiftReducer/decisionShiftSlice";
import { fetchSupervisors } from "../../redux/SupervisorReducer/supervisorSlice";
import { fetchSkills } from "../../redux/SkillReducer/skillSlice";
import useSkillLabels from '../../hooks/useSkillLabels';
import { fetchGigRequests } from "../../redux/GigRequestsReducer/gigRequestsSlice";
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import { transformCalendarData, transformGigRequestDetails } from '../../utils/calendarReportTransform';
import { parseShiftId } from '../../utils/shiftUtils';
import { ActivityLogEventActions } from '../../constants/activityLogEventActions';
import CalendarEventComponent from '../../components/calendar/CalendarEventComponent';
import CalendarCustomToolbar from '../../components/calendar/CalendarCustomToolbar';
import MobileWeekAgenda from '../../components/calendar/MobileWeekAgenda';
import MobileMonthView from '../../components/calendar/MobileMonthView';
import WorkerWeekSchedule from '../../components/worker-week-schedule/WorkerWeekSchedule';
import { ShiftEvent, AggregatedEvent } from '../../types/calendarTypes';
import { getStatusColor, statusLegend, aggregateEvents } from '../../utils/calendarUtils';
import { normalizeShiftType } from '../../utils/shiftTypeUtils';
import { formatTimeDisplay } from '../../utils/gigShiftUtils';
import shiftCardStyles from '../../components/shiftcard/shiftcard.module.css';

// ========================================
// CALENDAR SETUP
// ========================================
const localizer = momentLocalizer(moment);

// ========================================
// HELPER FUNCTIONS
// ========================================
const isPendingStatus = (status: string): boolean =>
    status === 'PENDING APPROVAL' || status === 'PND';

const CalendarReportView: React.FC = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const dispatch = useAppDispatch();
    const { data, loading, error } = useAppSelector((state) => state.calendarReport);
    const currentUser = useAppSelector((state) => state.currentUser.user);
    const { supervisors } = useAppSelector((state) => state.supervisors);
    const { skills } = useAppSelector((state) => state.skills);
    const { formatSkills } = useSkillLabels(currentUser?.siteCd);
    const gigShifts = useAppSelector((state) => state.gigRequests.details);
    const [searchParams] = useSearchParams();

    const [supervisorFilter, setSupervisorFilter] = useState<string>('all');
    const [grabbedByFilter, setGrabbedByFilter] = useState<string>('all');
    const [skillFilter, setSkillFilter] = useState<string[]>([]);
    const filterButtonRef = useRef<HTMLButtonElement | null>(null);
    const [filterOpen, setFilterOpen] = useState(false);

    const activeFilterCount =
        (supervisorFilter !== 'all' ? 1 : 0) +
        (grabbedByFilter !== 'all' ? 1 : 0) +
        (skillFilter.length > 0 ? 1 : 0);

    const clearAllFilters = () => {
        setSupervisorFilter('all');
        setGrabbedByFilter('all');
        setSkillFilter([]);
    };

    // Deep-link params from GigShiftCard "Review Calendar" action or
    // ShiftCard "Review" action from the approve/reject page.
    const requestId = searchParams.get('requestId');
    const seqId = searchParams.get('seqId');
    const dateParam = searchParams.get('date');
    const shiftTypeParam = searchParams.get('shiftType');

    const [currentDate, setCurrentDate] = useState<Date>(() => {
        if (dateParam) {
            const parsed = moment(dateParam);
            if (parsed.isValid()) return parsed.toDate();
        }
        return new Date();
    });
    const [view, setView] = useState<View>(() =>
        window.matchMedia('(max-width: 599.95px)').matches ? 'day' : 'month'
    );

    const [selectedEvent, setSelectedEvent] = useState<AggregatedEvent | null>(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [decisionLoading, setDecisionLoading] = useState<ShiftDecision | null>(null);
    const [hasAutoOpened, setHasAutoOpened] = useState(false);
    const [pendingDecision, setPendingDecision] = useState<ShiftDecision | null>(null);
    const [rejectionNotes, setRejectionNotes] = useState<string>('');
    const REJECTION_NOTES_MAX_LENGTH = 500;

    const rawEvents = useMemo(() => {
        const regular = data ? transformCalendarData(data) : [];
        const gig = gigShifts ? transformGigRequestDetails(gigShifts) : [];
        return [...regular, ...gig];
    }, [data, gigShifts]);

    const filteredEvents = useMemo(() => {
        return rawEvents.filter((e) => {
            if (supervisorFilter !== 'all' && e.postedByUserId !== supervisorFilter) return false;
            if (grabbedByFilter !== 'all' && e.claimedBy !== grabbedByFilter) return false;
            if (skillFilter.length > 0 && !skillFilter.includes(e.skillCd ?? '')) return false;
            return true;
        });
    }, [rawEvents, supervisorFilter, grabbedByFilter, skillFilter]);

    const aggregatedEvents = useMemo(() => {
        return aggregateEvents(filteredEvents);
    }, [filteredEvents]);

    const supervisorOptions = useMemo(() => {
        const opts = supervisors
            ?.slice()
            .sort((a, b) => `${a.firstName} ${a.lastName}`.localeCompare(`${b.firstName} ${b.lastName}`))
            .map((s) => ({
                label: `${s.firstName} ${s.lastName}`.trim(),
                value: s.workdayId,
            })) ?? [];
        return [{ label: 'All', value: 'all' }, ...opts];
    }, [supervisors]);

    const grabbedByOptions = useMemo(() => {
        const map = new Map<string, string>();
        rawEvents.forEach((e) => {
            if (e.claimedBy && e.claimedByName) {
                map.set(e.claimedBy, e.claimedByName);
            }
        });
        const opts = Array.from(map.entries())
            .map(([value, label]) => ({ label, value }))
            .sort((a, b) => a.label.localeCompare(b.label));
        return [{ label: 'All', value: 'all' }, ...opts];
    }, [rawEvents]);

    const skillOptions = useMemo(() => {
        return skills
            ?.slice()
            .sort((a, b) => a.lookupValueDesc.localeCompare(b.lookupValueDesc))
            .map((s) => ({ label: s.lookupValueDesc, value: s.lookupValueCd })) ?? [];
    }, [skills]);

    // Auto-open the shift dialog when arriving via a deep link (requestId + seqId).
    useEffect(() => {
        if (hasAutoOpened) return;
        if (!requestId || !seqId) return;
        if (aggregatedEvents.length === 0) return;

        const targetId = `${requestId}-${seqId}`;
        const gigTargetId = `gig-${targetId}`;
        const preferredTargetId = shiftTypeParam?.toUpperCase() === 'GIG' ? gigTargetId : targetId;
        const match = aggregatedEvents.find((agg) =>
            agg.resource.events.some((e) => e.id === preferredTargetId)
        ) ?? aggregatedEvents.find((agg) =>
            agg.resource.events.some((e) => e.id === targetId || e.id === gigTargetId)
        );
        if (match) {
            setSelectedEvent(match);
            setIsModalOpen(true);
            setHasAutoOpened(true);
        }
    }, [aggregatedEvents, requestId, seqId, shiftTypeParam, hasAutoOpened]);

    useEffect(() => {
        if (!currentUser?.siteCd) return;
        dispatch(fetchSupervisors(currentUser.siteCd));
        dispatch(fetchSkills(currentUser.siteCd));
    }, [dispatch, currentUser?.siteCd]);

    useEffect(() => {
        if (!currentUser?.siteCd) return;

        const m = moment(currentDate);
        let startDate: string;
        let endDate: string;

        if (view === 'month') {
            // Month view renders full weeks, so it can show leading/trailing
            // days from the adjacent months - include those in the range.
            startDate = m.clone().startOf('month').startOf('week').format('YYYY-MM-DD');
            endDate = m.clone().endOf('month').endOf('week').format('YYYY-MM-DD');
        } else if (view === 'week') {
            // Week view can span a month boundary.
            startDate = m.clone().startOf('week').format('YYYY-MM-DD');
            endDate = m.clone().endOf('week').format('YYYY-MM-DD');
        } else {
            startDate = m.clone().startOf('day').format('YYYY-MM-DD');
            endDate = m.clone().endOf('day').format('YYYY-MM-DD');
        }

        // Pad the requested range by an extra week on each side as a safety
        // margin against timezone/boundary edge cases.
        startDate = moment(startDate, 'YYYY-MM-DD').subtract(1, 'week').format('YYYY-MM-DD');
        endDate = moment(endDate, 'YYYY-MM-DD').add(1, 'week').format('YYYY-MM-DD');

        dispatch(fetchCalendarReport({
            siteCd: currentUser.siteCd,
            startDate,
            endDate
        }));
        dispatch(fetchGigRequests({ siteCd: currentUser.siteCd, statusFilter: 'ALL', startDate, endDate }));
    }, [dispatch, currentDate, view, currentUser?.siteCd]);

    const handleNavigate = useCallback((newDate: Date) => {
        setCurrentDate(newDate);
    }, []);

    const handleViewChange = useCallback((newView: View) => {
        setView(newView);
    }, []);

    // react-big-calendar's own toolbar normally computes PREV/NEXT/TODAY
    // dates internally based on the active view. The mobile week agenda
    // bypasses <Calendar> entirely, so we replicate that date math here.
    const handleMobileWeekNavigate = useCallback((action: 'PREV' | 'NEXT' | 'TODAY') => {
        if (action === 'TODAY') {
            setCurrentDate(new Date());
        } else if (action === 'PREV') {
            setCurrentDate((prev) => moment(prev).subtract(1, 'week').toDate());
        } else if (action === 'NEXT') {
            setCurrentDate((prev) => moment(prev).add(1, 'week').toDate());
        }
    }, []);

    // Same as above, but for the mobile month overview, which also bypasses
    // <Calendar> and needs its own PREV/NEXT/TODAY month math.
    const handleMobileMonthNavigate = useCallback((action: 'PREV' | 'NEXT' | 'TODAY') => {
        if (action === 'TODAY') {
            setCurrentDate(new Date());
        } else if (action === 'PREV') {
            setCurrentDate((prev) => moment(prev).subtract(1, 'month').toDate());
        } else if (action === 'NEXT') {
            setCurrentDate((prev) => moment(prev).add(1, 'month').toDate());
        }
    }, []);

    const handleSelectEvent = useCallback((event: AggregatedEvent) => {
        setSelectedEvent(event);
        setIsModalOpen(true);
    }, []);

    // If a week-day click's shift isn't found in the currently loaded month
    // (e.g. clicking a GIG requester's VTO/SFT shift that falls outside the
    // month currently shown on the calendar), we stash the candidate ids here,
    // navigate to that day's month to trigger a re-fetch, and retry the match
    // once the new aggregatedEvents come in.
    const [pendingWeekDayMatch, setPendingWeekDayMatch] = useState<string[] | null>(null);
    // Tracks whether the refetch triggered by a pending match has actually
    // started, so we don't give up on the very next render before `loading`
    // has had a chance to flip true.
    const pendingMatchLoadStarted = useRef(false);

    // Called when a user clicks a day (other than the anchor day) in the
    // WorkerWeekSchedule preview inside the shift details dialog. Finds the
    // matching aggregated calendar event for that day's shift(s) and swaps
    // the dialog over to show those details instead.
    const handleWeekDayClick = useCallback((dayShifts: { requestId?: number | string; requestSeqId?: number | string; shiftType?: string; requestType?: string }[], date: Date) => {
        if (dayShifts.length === 0) return;
        const candidateIds = dayShifts.map((s) => {
            const isGig = (s.shiftType || s.requestType) === 'GIG';
            return isGig ? `gig-${s.requestId}-${s.requestSeqId}` : `${s.requestId}-${s.requestSeqId}`;
        });
        const match = aggregatedEvents.find((agg) =>
            agg.resource.events.some((e) => candidateIds.includes(e.id))
        );
        if (match) {
            setSelectedEvent(match);
            setIsModalOpen(true);
            return;
        }

        // Not found in the currently loaded month - navigate there and retry
        // once the calendar data refetches for that month.
        if (!moment(date).isSame(currentDate, 'month')) {
            pendingMatchLoadStarted.current = false;
            setPendingWeekDayMatch(candidateIds);
            setCurrentDate(date);
        } else {
            // Same month, still no match - nothing more we can do.
            toast.error('Could not find that shift.');
        }
    }, [aggregatedEvents, currentDate]);

    // Retry a pending week-day match once aggregatedEvents refreshes for the
    // newly-navigated month. If the refetch finishes loading and we still
    // can't find the shift, give up and let the user know instead of
    // silently leaving the click as a no-op.
    useEffect(() => {
        if (!pendingWeekDayMatch) return;
        if (loading) {
            pendingMatchLoadStarted.current = true;
            return;
        }
        const match = aggregatedEvents.find((agg) =>
            agg.resource.events.some((e) => pendingWeekDayMatch.includes(e.id))
        );
        if (match) {
            setSelectedEvent(match);
            setIsModalOpen(true);
            setPendingWeekDayMatch(null);
        } else if (pendingMatchLoadStarted.current) {
            // The refetch for the new month has completed and still no match.
            setPendingWeekDayMatch(null);
            toast.error('Could not find that shift.');
        }
    }, [pendingWeekDayMatch, aggregatedEvents, loading]);

    const handleCloseModal = useCallback(() => {
        setIsModalOpen(false);
        setSelectedEvent(null);
    }, []);

    const refetchCalendar = useCallback(() => {
        if (!currentUser?.siteCd) return;
        const startDate = moment(currentDate).startOf('month').format('YYYY-MM-DD');
        const endDate = moment(currentDate).endOf('month').format('YYYY-MM-DD');
        dispatch(fetchCalendarReport({ siteCd: currentUser.siteCd, startDate, endDate }));
        dispatch(fetchGigRequests({ siteCd: currentUser.siteCd, statusFilter: 'ALL', startDate, endDate }));
    }, [dispatch, currentDate, currentUser?.siteCd]);

    const handleDecision = useCallback(async (decision: ShiftDecision, notes?: string) => {
        if (!selectedEvent || !currentUser) return;

        const pendingShifts = selectedEvent.resource.events.filter(
            (s) => isPendingStatus(s.status)
        );
        if (pendingShifts.length === 0) {
            toast.error('No pending shifts to update.');
            return;
        }

        const supervisorName = `${currentUser.firstName ?? ''} ${currentUser.lastName ?? ''}`.trim();
        const eventAction =
            decision === 'approve'
                ? [ActivityLogEventActions.ShiftApproved]
                : [ActivityLogEventActions.ShiftDenied, ActivityLogEventActions.ShiftPosted];

        setDecisionLoading(decision);
        try {
            if (selectedEvent.resource.shiftType === 'GIG') {
                for (const shift of pendingShifts) {
                    if (shift.requestId == null || shift.requestSeqId == null) {
                        toast.error('Cannot process: Gig request information is missing.');
                        return;
                    }

                    await dispatch(decideShift({
                        decision,
                        payload: {
                            requestId: shift.requestId,
                            requestSeqId: shift.requestSeqId,
                            requestType: 'G',
                            requestSite: currentUser.siteCd,
                            affectedUserId: shift.postedByUserId ?? selectedEvent.resource.postedByUserId,
                            affectedUserName: shift.postedByUserNm ?? selectedEvent.resource.postedByUserNm,
                            decidedById: currentUser.workdayId ?? currentUser.userId,
                            decidedByName: supervisorName,
                            isGigRequest: true,
                            rejectionNotes: decision === 'reject' ? notes : undefined,
                        },
                    })).unwrap();
                }

                toast.success(
                    decision === 'approve'
                        ? 'Gig shift request(s) approved.'
                        : 'Gig shift request(s) rejected.'
                );
                handleCloseModal();
                refetchCalendar();
                return;
            }

            for (const shift of pendingShifts) {
                if (!shift.claimedByName) {
                    toast.error('Cannot process: Claimed by user is missing on a shift.');
                    continue;
                }
                const { requestId: rId, requestSeqId } = parseShiftId(shift.id);
                await dispatch(
                    decideShift({
                        decision,
                        payload: {
                            requestId: rId,
                            requestSeqId,
                            requestType: shift.shiftType,
                            requestSite: currentUser.siteCd,
                            affectedUserId: shift.claimedBy,
                            affectedUserName: shift.claimedByName,
                            decidedById: currentUser.workdayId,
                            decidedByName: supervisorName,
                            eventAction,
                            rejectionNotes: decision === 'reject' ? notes : undefined,
                        },
                    })
                ).unwrap();
            }
            toast.success(
                decision === 'approve'
                    ? 'Shift claim(s) approved.'
                    : 'Shift claim(s) rejected. The shift(s) have been re-posted.'
            );
            handleCloseModal();
            refetchCalendar();
        } catch (err) {
            const msg = err instanceof Error ? err.message : `Failed to ${decision} shift`;
            toast.error(msg);
        } finally {
            setDecisionLoading(null);
        }
    }, [selectedEvent, currentUser, dispatch, handleCloseModal, refetchCalendar]);

    const canApproveRejectSelection = selectedEvent?.resource.events.some((event) =>
        isPendingStatus(event.status)
    ) ?? false;

    const currentShiftIds = useMemo(
        () => selectedEvent?.resource.events.map((e) => e.id) ?? [],
        [selectedEvent]
    );

    const eventStyleGetter = (event: AggregatedEvent) => {
        return {
            style: {
                backgroundColor: getStatusColor(event.resource.status),
                borderRadius: '4px',
                opacity: 0.95,
                color: 'black',
                border: 'none',
                display: 'block'
            }
        };
    };

    const filterSlot = useMemo(() => (
        <Tooltip title="Filters">
            <IconButton
                size="small"
                ref={filterButtonRef}
                onClick={() => setFilterOpen(true)}
                sx={{ color: '#CC0000', '&:hover': { backgroundColor: 'rgba(204, 0, 0, 0.08)' } }}
            >
                <Badge badgeContent={activeFilterCount} color="error">
                    <FilterListIcon />
                </Badge>
            </IconButton>
        </Tooltip>
    ), [activeFilterCount]);

    // Tapping a day in the simplified mobile month view drills straight
    // into day view for that date, where shift details are fully legible.
    const handleMobileMonthSelectDay = useCallback((selectedDate: Date) => {
        setCurrentDate(selectedDate);
        setView('day');
    }, []);

    const calendarComponents = useMemo(() => ({
        toolbar: (toolbarProps: any) => (
            <CalendarCustomToolbar
                {...toolbarProps}
                filterSlot={filterSlot}
            />
        ),
        event: (props: any) => <CalendarEventComponent {...props} variant="detailed" calendarView={view} />
    }), [filterSlot, view]);

    // On mobile, week view swaps react-big-calendar's native 7-column grid
    // (too narrow to show useful shift details on a phone) for a vertically
    // scrollable day-by-day agenda reusing the same detailed event cards
    // from day view.
    const isMobileWeekAgenda = isMobile && view === 'week';
    // On mobile, month view swaps the native grid (which crams full event
    // chips into ~50px cells) for a simplified dot-based overview. Tapping a
    // day jumps into day view for full details.
    const isMobileMonthView = isMobile && view === 'month';

    if (loading && !data) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
                <CircularProgress />
            </Box>
        );
    }

    if (error && !data) {
        return (
            <Box sx={{ p: 2 }}>
                <Alert severity="error">Error loading calendar data: {error}</Alert>
            </Box>
        );
    }

    return (
        <Box sx={{ width: '100%', p: { xs: 1, sm: 2 }, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box className={`calendar-report-container${(isMobileWeekAgenda || isMobileMonthView) ? ' calendar-report-container--day' : ''}`}>
                <div className="calendar-wrapper">
                    {isMobileWeekAgenda ? (
                        <>
                            <CalendarCustomToolbar
                                date={currentDate}
                                view={view}
                                onNavigate={handleMobileWeekNavigate}
                                onView={handleViewChange}
                                filterSlot={filterSlot}
                            />
                            <MobileWeekAgenda
                                date={currentDate}
                                events={aggregatedEvents}
                                onSelectEvent={handleSelectEvent}
                            />
                        </>
                    ) : isMobileMonthView ? (
                        <>
                            <CalendarCustomToolbar
                                date={currentDate}
                                view={view}
                                onNavigate={handleMobileMonthNavigate}
                                onView={handleViewChange}
                                filterSlot={filterSlot}
                            />
                            <MobileMonthView
                                date={currentDate}
                                events={aggregatedEvents}
                                onSelectDay={handleMobileMonthSelectDay}
                            />
                        </>
                    ) : (
                        <Calendar
                            localizer={localizer}
                            events={aggregatedEvents}
                            startAccessor="start"
                            endAccessor="end"
                            view={view}
                            onView={handleViewChange}
                            date={currentDate}
                            onNavigate={handleNavigate}
                            components={calendarComponents}
                            eventPropGetter={eventStyleGetter}
                            onSelectEvent={handleSelectEvent}
                            views={['month', 'week', 'day']}
                            popup={true}
                            showAllEvents={true}
                        />
                    )}
                </div>
            </Box>

            <Popover
                open={filterOpen}
                anchorEl={() => filterButtonRef.current as HTMLElement}
                onClose={() => setFilterOpen(false)}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                transformOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                PaperProps={{
                    sx: {
                        p: 2,
                        width: { xs: 'calc(100vw - 32px)', sm: 320 },
                        maxWidth: 320,
                        mt: -1
                    }
                }}
            >
                <Stack spacing={2}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Filters</Typography>
                        <MuiButton
                            onClick={clearAllFilters}
                            size="small"
                            colors="secondary"
                            variant="outlined"
                            disabled={activeFilterCount === 0}
                        >
                            Clear
                        </MuiButton>
                    </Stack>
                    <Divider />
                    <Stack spacing={0.5}>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>Supervisor</Typography>
                        <MuiSelector
                            value={supervisorFilter}
                            options={supervisorOptions}
                            onChange={(v: string) => setSupervisorFilter(v)}
                            placeholder="Select..."
                        />
                    </Stack>
                    <Stack spacing={0.5}>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>Grabbed By</Typography>
                        <MuiSelector
                            value={grabbedByFilter}
                            options={grabbedByOptions}
                            onChange={(v: string) => setGrabbedByFilter(v)}
                            placeholder="Select..."
                        />
                    </Stack>
                    <Stack spacing={0.5}>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>Skill</Typography>
                        <FormControl fullWidth>
                            <Select
                                multiple
                                displayEmpty
                                value={skillFilter}
                                onChange={(e) => setSkillFilter(e.target.value as string[])}
                                renderValue={(selected) => {
                                    if ((selected).length === 0) {
                                        return <span style={{ color: '#888' }}>All</span>;
                                    }
                                    return (
                                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                                            {(selected).map((val) => (
                                                <span
                                                    key={val}
                                                    style={{
                                                        backgroundColor: '#ffebee',
                                                        color: '#000',
                                                        padding: '2px 8px',
                                                        borderRadius: '12px',
                                                        fontSize: '0.85rem',
                                                        display: 'inline-block',
                                                    }}
                                                >
                                                    {skillOptions.find(opt => opt.value === val)?.label}
                                                </span>
                                            ))}
                                        </div>
                                    );
                                }}
                                sx={{
                                    height: 'auto',
                                    minHeight: '2.75rem',
                                    borderRadius: 'calc(var(--radius) - 0.125rem)',
                                    backgroundColor: '#fff',
                                    '& .MuiSelect-select': {
                                        paddingTop: '8px',
                                        paddingBottom: '8px',
                                    },
                                }}
                                MenuProps={{
                                    PaperProps: {
                                        sx: {
                                            maxHeight: 300,
                                            '& .MuiMenuItem-root.Mui-selected': {
                                                backgroundColor: 'rgba(0, 0, 0, 0.08)',
                                                '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.12)' },
                                            },
                                        },
                                    },
                                }}
                            >
                                {skillOptions.map((option) => (
                                    <MenuItem key={option.value} value={option.value}>
                                        {option.label}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Stack>
                </Stack>
            </Popover>

            <Dialog
                open={isModalOpen}
                onClose={handleCloseModal}
                maxWidth="lg"
                fullWidth
                fullScreen={isMobile}
            >
                <DialogTitle sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    borderBottom: '1px solid #e0e0e0',
                    pb: 2
                }}>
                    {selectedEvent?.resource.shiftType === 'GIG' ? (
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                            Shift Details - {selectedEvent && moment(selectedEvent.start).format('MMMM D, YYYY')}
                            {(selectedEvent?.resource.events.length ?? 0) > 1 ? ` (${selectedEvent?.resource.events.length ?? 0})` : ''} - {selectedEvent?.resource.events.reduce(
                                (sum, e) => sum + (typeof e.requestHours === 'number' ? e.requestHours : 0),
                                0
                            )} Hours
                        </Typography>
                    ) : (
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                            Shift Details - {selectedEvent && moment(selectedEvent.start).format('MMMM D, YYYY')}
                            {(selectedEvent?.resource.events.length ?? 0) > 1 ? ` (${selectedEvent?.resource.events.length ?? 0} Shifts)` : ''}
                        </Typography>
                    )}
                    <IconButton onClick={handleCloseModal} size="small">
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent sx={{ mt: 2 }}>
                    {selectedEvent && (
                        <Box>
                            <Box sx={{ mb: 3, p: 2, backgroundColor: '#f9f9f9', borderRadius: '8px' }}>
                                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                                    Summary
                                </Typography>
                                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 2 }}>
                                    <Box>
                                        <Typography variant="body2" sx={{ color: '#666', fontSize: '0.875rem' }}>
                                            Shift Type
                                        </Typography>
                                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                            {selectedEvent.resource.shiftType}
                                        </Typography>
                                    </Box>
                                    {selectedEvent.resource.team && (
                                        <Box>
                                            <Typography variant="body2" sx={{ color: '#666', fontSize: '0.875rem' }}>
                                                Team
                                            </Typography>
                                            <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                                {selectedEvent.resource.team}
                                            </Typography>
                                        </Box>
                                    )}
                                    {(selectedEvent.resource.skillCd || selectedEvent.resource.skillDesc) && (
                                        <Box>
                                            <Typography variant="body2" sx={{ color: '#666', fontSize: '0.875rem' }}>
                                                Skills
                                            </Typography>
                                            <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                                {formatSkills(selectedEvent.resource.skillCd) || selectedEvent.resource.skillDesc}
                                            </Typography>
                                        </Box>
                                    )}
                                    <Box>
                                        <Typography variant="body2" sx={{ color: '#666', fontSize: '0.875rem' }}>
                                            Status
                                        </Typography>
                                        <Chip
                                            label={statusLegend.find(s => s.status === selectedEvent.resource.status)?.label || selectedEvent.resource.status}
                                            size="small"
                                            sx={{
                                                backgroundColor: getStatusColor(selectedEvent.resource.status),
                                                color: 'white',
                                                fontWeight: 600,
                                                fontSize: '0.75rem',
                                                mt: 0.5
                                            }}
                                        />
                                    </Box>
                                    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 2, gridColumn: '1 / -1' }}>
                                        <Box>
                                            <Typography variant="body2" sx={{ color: '#666', fontSize: '0.875rem' }}>
                                                Start Time
                                            </Typography>
                                            <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                                {formatTimeDisplay(selectedEvent.resource.events[0]?.startTime) || '—'}
                                            </Typography>
                                        </Box>
                                        <Box>
                                            <Typography variant="body2" sx={{ color: '#666', fontSize: '0.875rem' }}>
                                                End Time
                                            </Typography>
                                            <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                                {formatTimeDisplay(selectedEvent.resource.events[0]?.endTime) || '—'}
                                            </Typography>
                                        </Box>
                                    </Box>
                                    <Box>
                                        <Typography variant="body2" sx={{ color: '#666', fontSize: '0.875rem' }}>
                                            Posted By
                                        </Typography>
                                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                            {selectedEvent.resource.postedByUserNm || '—'}
                                        </Typography>
                                    </Box>
                                    {selectedEvent.resource.shiftType !== 'GIG' && selectedEvent.resource.status !== 'POSTED' && (
                                        <Box>
                                            <Typography variant="body2" sx={{ color: '#666', fontSize: '0.875rem' }}>
                                                Claimed By
                                            </Typography>
                                            <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                                {selectedEvent.resource.claimedByUserNm || '—'}
                                            </Typography>
                                        </Box>
                                    )}
                                </Box>
                                {selectedEvent.resource.status === 'REJECTED' && (() => {
                                    const rejectionNotes = selectedEvent.resource.events.find(
                                        (e) => e.rejectionNotes
                                    )?.rejectionNotes;
                                    if (!rejectionNotes) return null;
                                    return (
                                        <Box sx={{
                                            mt: 2,
                                            p: 1.5,
                                            backgroundColor: 'rgba(212, 0, 41, 0.06)',
                                            borderLeft: '3px solid #D40029',
                                            borderRadius: '0 6px 6px 0',
                                        }}>
                                            <Typography variant="body2" sx={{ color: '#D40029', fontWeight: 600, fontSize: '0.875rem' }}>
                                                Rejection Notes
                                            </Typography>
                                            <Typography variant="body1" sx={{ fontWeight: 500, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                                                {rejectionNotes}
                                            </Typography>
                                        </Box>
                                    );
                                })()}
                            </Box>
                            
                            {(() => {
                                const siteCd = currentUser?.siteCd ?? '';

                                if (selectedEvent.resource.shiftType === 'GIG') {
                                    const requesterId = selectedEvent.resource.postedByUserId
                                        || selectedEvent.resource.events.find((e) => e.postedByUserId)?.postedByUserId;
                                    const requesterName = selectedEvent.resource.postedByUserNm
                                        || selectedEvent.resource.events.find((e) => e.postedByUserNm)?.postedByUserNm
                                        || requesterId;

                                    if (!requesterId || !requesterName || !siteCd) return null;
                                    return (
                                        <WorkerWeekSchedule
                                            userId={requesterId}
                                            userName={requesterName}
                                            siteCd={siteCd}
                                            anchorDate={selectedEvent.start}
                                            currentShiftIds={currentShiftIds}
                                            onDayClick={handleWeekDayClick}
                                        />
                                    );
                                }

                                const workerShift = selectedEvent.resource.events.find(
                                    (e) => !!e.claimedBy && !!e.claimedByName
                                );
                                const workerShiftUserId = String(workerShift?.claimedBy ?? '');
                                const workerShiftUserName = String(workerShift?.claimedByName ?? '');
                                if (!workerShiftUserId || !workerShiftUserName || !siteCd) return null;
                                return (
                                    <WorkerWeekSchedule
                                        userId={workerShiftUserId}
                                        userName={workerShiftUserName}
                                        siteCd={siteCd}
                                        anchorDate={selectedEvent.start}
                                        currentShiftIds={currentShiftIds}
                                        onDayClick={handleWeekDayClick}
                                    />
                                );
                            })()}
                        </Box>
                    )}
                </DialogContent>
                <DialogActions sx={{ p: 2, borderTop: '1px solid #e0e0e0', justifyContent: 'space-between' }}>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        {canApproveRejectSelection && (
                            <>
                                <MuiButton
                                    onClick={() => { setRejectionNotes(''); setPendingDecision('approve'); }}
                                    disabled={decisionLoading !== null}
                                >
                                    {decisionLoading === 'approve' ? 'Approving...' : 'Approve'}
                                </MuiButton>
                                <MuiButton
                                    onClick={() => { setRejectionNotes(''); setPendingDecision('reject'); }}
                                    colors="secondary"
                                    variant="outlined"
                                    disabled={decisionLoading !== null}
                                >
                                    {decisionLoading === 'reject' ? 'Rejecting...' : 'Reject'}
                                </MuiButton>
                            </>
                        )}
                    </Box>
                    <MuiButton onClick={handleCloseModal} colors="secondary" variant="outlined">
                        Close
                    </MuiButton>
                </DialogActions>
            </Dialog>

            {/* Approve/Reject Confirmation Dialog (matches ShiftCard confirmation) */}
            <Dialog
                open={pendingDecision !== null}
                onClose={() => (decisionLoading ? null : setPendingDecision(null))}
            >
                <div>
                    {pendingDecision === 'approve' && (
                        <div className={shiftCardStyles.confirmCardHeader}>
                            <Typography variant="h5" className={shiftCardStyles.cardTitle}>Request Approval</Typography>
                            <p className={shiftCardStyles.cardSubtitle}>Please review and confirm approval</p>
                        </div>
                    )}
                    {pendingDecision === 'reject' && (
                        <div className={shiftCardStyles.confirmCardHeader}>
                            <Typography variant="h5" className={shiftCardStyles.cardTitle}>Request Rejection</Typography>
                            <p className={shiftCardStyles.cardSubtitle}>Please review and confirm rejection</p>
                        </div>
                    )}
                    <hr style={{ border: 'none', borderTop: '1px solid var(--color-border)' }} />
                    {selectedEvent && (
                        <>
                            <div className={shiftCardStyles.confirmWrapper}>
                                <div className={shiftCardStyles.cardShiftHeader}>
                                    <div className={shiftCardStyles.cardShiftTitle}>
                                        <Typography variant="h6" className={shiftCardStyles.cardTitle}>
                                            {selectedEvent.resource.skillDesc || selectedEvent.title}
                                        </Typography>
                                    </div>
                                    <div className={shiftCardStyles.chipContainer}>
                                        {selectedEvent.resource.shiftType && (
                                            <MuiChip
                                                colors="default"
                                                label={(() => {
                                                    const base = normalizeShiftType(selectedEvent.resource.shiftType);
                                                    if (base === 'WKR') return 'Worker';
                                                    if (base === 'VTO') return 'VTO';
                                                    if (base === 'SFT') return 'Shift';
                                                    return base;
                                                })()}
                                            />
                                        )}
                                    </div>
                                </div>
                                <div className={shiftCardStyles.detailsGrid}>
                                    {currentUser?.siteCd && (
                                        <div className={shiftCardStyles.detailRow}>
                                            <LocationPin fontSize="small" className={shiftCardStyles.detailIcon} />
                                            <span>{currentUser.siteCd}</span>
                                        </div>
                                    )}
                                    <div className={shiftCardStyles.detailRow}>
                                        <CalendarMonth fontSize="small" className={shiftCardStyles.detailIcon} />
                                        <span>
                                            {moment(selectedEvent.start).format('dddd, MMMM D, YYYY')}
                                        </span>
                                    </div>
                                    <div className={shiftCardStyles.detailRow}>
                                        <AccessTime fontSize="small" className={shiftCardStyles.detailIcon} />
                                        <span>{selectedEvent.resource.time}</span>
                                    </div>
                                    <div className={shiftCardStyles.detailRow}>
                                        <Person fontSize="small" className={shiftCardStyles.detailIcon} />
                                        <span>Posted by: {selectedEvent.resource.postedByUserNm || '—'}</span>
                                    </div>
                                    {(() => {
                                        const claimants = Array.from(new Set(
                                            selectedEvent.resource.events
                                                .filter(e => isPendingStatus(e.status) && e.claimedByName)
                                                .map(e => e.claimedByName)
                                        ));
                                        if (claimants.length === 0) return null;
                                        return (
                                            <div className={shiftCardStyles.detailRow}>
                                                <Person fontSize="small" className={shiftCardStyles.detailIcon} />
                                                <span>Claimed by: {claimants.join(', ')}</span>
                                            </div>
                                        );
                                    })()}
                                </div>
                            </div>
                            {(() => {
                                const notes = selectedEvent.resource.events.find(e => (e as any).notes)?.['notes' as keyof ShiftEvent] as string | undefined;
                                return notes ? (
                                    <div className={shiftCardStyles.notesSection}>
                                        <div className={shiftCardStyles.notesLabel}>Notes:</div>
                                        <p className={shiftCardStyles.notesText}>{notes}</p>
                                    </div>
                                ) : (
                                    <div className={shiftCardStyles.notesSectionEmpty}></div>
                                );
                            })()}
                            {pendingDecision === 'reject' && (
                                <div className={shiftCardStyles.rejectionNotesSection}>
                                    <label className={shiftCardStyles.notesLabel} htmlFor="calendarRejectionNotes">
                                        Rejection Notes (Optional)
                                    </label>
                                    <textarea
                                        id="calendarRejectionNotes"
                                        className={shiftCardStyles.rejectionNotesTextarea}
                                        placeholder="Provide a reason for rejection..."
                                        value={rejectionNotes}
                                        maxLength={REJECTION_NOTES_MAX_LENGTH}
                                        rows={3}
                                        onChange={(e) => setRejectionNotes(e.target.value)}
                                    />
                                    <p className={shiftCardStyles.rejectionNotesCounter}>
                                        {rejectionNotes.length} / {REJECTION_NOTES_MAX_LENGTH}
                                    </p>
                                </div>
                            )}
                        </>
                    )}
                    <div className={shiftCardStyles.closeConfirmButton}>
                        <MuiButton
                            onClick={() => { setPendingDecision(null); setRejectionNotes(''); }}
                            colors="tertiaryText"
                            disabled={decisionLoading !== null}
                        >
                            Cancel
                        </MuiButton>
                        <MuiButton
                            onClick={async () => {
                                if (!pendingDecision) return;
                                const decision = pendingDecision;
                                await handleDecision(decision, decision === 'reject' ? rejectionNotes : undefined);
                                setPendingDecision(null);
                                setRejectionNotes('');
                            }}
                            disabled={decisionLoading !== null}
                        >
                            Confirm
                        </MuiButton>
                    </div>
                </div>
            </Dialog>

            {/* Status Legend */}
            <Box sx={{
                py: 1,
                px: 1.5,
                backgroundColor: '#f9f9f9',
                borderRadius: '6px',
                border: '1px solid #e0e0e0',
            }}>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: '#333', fontSize: '0.8125rem', mr: 1 }}>
                        Status:
                    </Typography>
                    {statusLegend.map((item) => (
                        <Box key={item.status} sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                            <Box sx={{
                                width: '14px',
                                height: '14px',
                                backgroundColor: getStatusColor(item.status),
                                borderRadius: '3px',
                                flexShrink: 0
                            }} />
                            <Typography variant="body2" sx={{ fontSize: '0.8125rem', color: '#666' }}>
                                {item.label}
                            </Typography>
                        </Box>
                    ))}
                </Box>
            </Box>
        </Box>
    );
};

export default CalendarReportView;